import { machineMessageStoreApi } from "@/api/machineMessageStoreApi";
import socket from "@/api/socket";
import _ from "lodash";

const channelsCallbackMap = {};

const pushCallbackForChannel = (channel, callback) => {
  if (!channelsCallbackMap[channel]) {
    channelsCallbackMap[channel] = [];
  }
  channelsCallbackMap[channel].push(callback);
};

const removeCallbackForChannel = channel => delete channelsCallbackMap[channel];

const isAlreadySubscribedToChannelWithMachineId = (machineId, channel) =>
  _.has(channelsCallbackMap, api.channelWithMachineId(machineId, channel));

const removeAllCallbacks = () => Object.keys(channelsCallbackMap).forEach(removeCallbackForChannel);

const applyMessage = message => {
  if (!message.channel || !channelsCallbackMap[message.channel]) {
    return;
  }
  const channel = message.channel;
  channelsCallbackMap[channel].map(callback => {
    callback(message);
  });
};

const subscribe = (machineId, channel, filters, callback) => {
  const formattedChannel = api.channelWithMachineId(machineId, channel);
  socket.emit("joinChannel", formattedChannel);
  pushCallbackForChannel(formattedChannel, callback);
};

const retrieveAllDataFromMessageStore = async (machineId, channel, filters) => {
  return await machineMessageStoreApi.getData(machineId, channel, filters, true);
};

const retrieveScalarDataFromMessageStore = async (machineId, channel, filters) => {
  const messageStoreData = await retrieveAllDataFromMessageStore(machineId, channel, filters);
  messageStoreData.data = _.get(messageStoreData, "data.0", null);
  return messageStoreData;
};

const retrieveChannelsFromMessageStore = async (machineId, channelWildcard) => {
  return await machineMessageStoreApi.getChannels(machineId, channelWildcard);
};

const queryAllFromMessageStoreAndSubscribe = async (machineId, channel, filters, callback) => {
  const machineMessageStoreData = await retrieveAllDataFromMessageStore(
    machineId,
    channel,
    filters
  );
  if (!isMessageStoreResponseDataEmpty(machineMessageStoreData)) {
    callback(machineMessageStoreData);
  }
  subscribe(machineId, channel, {}, callback);
};

const queryScalarFromMessageStoreAndSubscribe = async (machineId, channel, filters, callback) => {
  const machineMessageStoreData = await retrieveScalarDataFromMessageStore(
    machineId,
    channel,
    filters
  );
  if (!isMessageStoreResponseDataEmpty(machineMessageStoreData)) {
    callback(machineMessageStoreData);
  }
  subscribe(machineId, channel, {}, callback);
};

const isMessageStoreResponseDataEmpty = response => _.isEmpty(response.data);

const reJoinAllChannels = () => {
  _.forOwn(channelsCallbackMap, (callbacks, channel) => {
    socket.emit("joinChannel", channel);
  });
};

const api = {
  channelWildcardToChannels: async (machineId, channelWildcard) => {
    if (channelWildcard.endsWith("/+")) {
      const channels = await retrieveChannelsFromMessageStore(machineId, channelWildcard);
      return Array.isArray(channels) ? channels : [];
    }

    if (channelWildcard.indexOf("/+/") > -1) {
      const channelWildcardSplitted = channelWildcard.split("/+/");
      const channels = await api.channelWildcardToChannels(
        machineId,
        channelWildcardSplitted[0] + "/+"
      );
      return channels.map(channel => channel + "/" + channelWildcardSplitted[1]);
    }

    return [channelWildcard];
  },
  subAny: async (machineId, channelWildcard, callback) => {
    const channels = await api.channelWildcardToChannels(machineId, channelWildcard);
    await Promise.all(
      channels.map(channel => {
        queryAllFromMessageStoreAndSubscribe(machineId, channel, { limit: 1 }, callback);
      })
    );
  },
  subLatestOnChannelsAfterDate: async (machineId, channelWildcard, startingFromDate, callback) => {
    const channels = await api.channelWildcardToChannels(machineId, channelWildcard);
    await Promise.all(
      channels.map(channel => {
        queryAllFromMessageStoreAndSubscribe(
          machineId,
          channel,
          { after: startingFromDate },
          callback
        );
      })
    );
  },
  subLastHrs: async (machineId, channel, hours, callback) => {
    const date = new Date();
    date.setHours(date.getHours() - hours);
    return api.subLatestOnChannelsAfterDate(machineId, channel, date, callback);
  },
  subLastCount: async (machineId, channel, count, callback) =>
    queryAllFromMessageStoreAndSubscribe(
      machineId,
      channel,
      { limit: !count ? 1 : count },
      callback
    ),
  subFrom: async (machineId, channel, startingFromDate, callback) =>
    queryAllFromMessageStoreAndSubscribe(machineId, channel, { after: startingFromDate }, callback),
  subLatest: async (machineId, channel, callback) =>
    queryScalarFromMessageStoreAndSubscribe(machineId, channel, { limit: 1 }, callback),
  subscribe: async (machineId, channel, callback) => subscribe(machineId, channel, {}, callback),
  watchCpData: (machineId, channel, callback) => {
    if (isAlreadySubscribedToChannelWithMachineId(machineId, channel)) {
      api.unsub(machineId, channel);
    }
    subscribe(machineId, channel, {}, callback);
  },
  unsub: (machineId, channel, callback) => {
    const formattedChannel = api.channelWithMachineId(machineId, channel);
    socket.emit("leaveChannel", formattedChannel);
    removeCallbackForChannel(formattedChannel);
    !_.isFunction(callback) || callback();
  },
  retrieveHistorical: async (machineId, channel, filters, callback) => {
    const result = await retrieveAllDataFromMessageStore(machineId, channel, filters);
    callback(result.data);
  },
  getChannels: async (machineId, channelWildcard) => {
    return api.channelWildcardToChannels(machineId, channelWildcard);
  },
  getQueryDataLast: async (machineId, channel) => {
    return await retrieveScalarDataFromMessageStore(machineId, channel, { limit: 1 });
  },
  getQueryData: async filters => {
    return await retrieveAllDataFromMessageStore(filters.of, filters.channel, filters);
  },
  messageReceived: applyMessage,
  unsubAll: () => {
    socket.emit("leaveAllChannels");
    removeAllCallbacks();
  },
  channelWithMachineId: (machineId, channel) => `${machineId}/${channel}`,
  close: () => console.warn("machineRealtimeApi.close method is not implemented."),
  changeAuth: () => console.warn("machineRealtimeApi.changeAuth method is not implemented."),
  cpEvents: () => console.warn("machineRealtimeApi.cpEvents method is not implemented."),
  pub: () => console.warn("machineRealtimeApi.pub method is not implemented.")
};

socket.on("event", api.messageReceived);

socket.on("reconnect", () => {
  reJoinAllChannels();
});

export default api;
